import { CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { Pair } from '@kodiak-finance/v2-sdk'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import { transparentize } from 'polished'
import { useState } from 'react'
import { Text } from 'rebass'
import styled from 'styled-components'

import { ExternalLink } from '../../theme'
import { unwrappedToken } from '../../utils/unwrappedToken'
import { ButtonSecondary } from '../Button'
import { LightCard } from '../Card'
import { AutoColumn } from '../Column'
import DoubleCurrencyLogo from '../DoubleLogo'
import { RowBetween, RowFixed } from '../Row'

export const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`

const StyledPositionCard = styled(LightCard)<{ bgColor: any }>`
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme, bgColor }) =>
    `radial-gradient(91.85% 100% at 1.84% 0%, ${transparentize(0.8, bgColor)} 0%, ${transparentize(
      0.8,
      theme.cardSecondary
    )} 100%) `};
  position: relative;
  overflow: hidden;
`

interface PositionCardProps {
  pair: Pair
  showUnwrapped?: boolean
  border?: string
  totalSupply?: CurrencyAmount<Token>
  liquidityTokenBalance?: CurrencyAmount<Token>
  stakedBalance?: CurrencyAmount<Token> // optional balance to indicate that liquidity is deposited in mining pool
}

export function MinimalPositionCard({
  pair,
  totalSupply: totalPoolTokens,
  liquidityTokenBalance: userPoolBalance,
  showUnwrapped = false,
  border,
}: PositionCardProps) {
  const { account } = useConnection()

  const currency0 = showUnwrapped ? pair.token0 : unwrappedToken(pair.token0)
  const currency1 = showUnwrapped ? pair.token1 : unwrappedToken(pair.token1)

  const [showMore, setShowMore] = useState(false)

  const poolTokenPercentage =
    !!userPoolBalance &&
    !!totalPoolTokens &&
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? new Percent(userPoolBalance.quotient, totalPoolTokens.quotient)
      : undefined

  const [token0Deposited, token1Deposited] =
    !!pair &&
    !!totalPoolTokens &&
    !!userPoolBalance &&
    // this condition is a short-circuit in the case where useTokenBalance updates sooner than useTotalSupply
    JSBI.greaterThanOrEqual(totalPoolTokens.quotient, userPoolBalance.quotient)
      ? [
          pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
          pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
        ]
      : [undefined, undefined]

  return (
    <>
      {
        userPoolBalance && JSBI.greaterThan(userPoolBalance.quotient, JSBI.BigInt(0)) ? (
          <LightCard border={border}>
            <AutoColumn gap="12px">
              <FixedHeightRow>
                <RowFixed>
                  <Text fontWeight={500} fontSize={16}>
                    <>Your position</>
                  </Text>
                </RowFixed>
              </FixedHeightRow>
              <FixedHeightRow onClick={() => setShowMore(!showMore)}>
                <RowFixed>
                  <DoubleCurrencyLogo currency0={currency0} currency1={currency1} margin={true} size={20} />
                  <Text fontWeight={500} fontSize={20}>
                    {currency0.symbol}/{currency1.symbol}
                  </Text>
                </RowFixed>
                <RowFixed>
                  <Text fontWeight={500} fontSize={20}>
                    {userPoolBalance ? userPoolBalance.toSignificant(4) : '-'}
                  </Text>
                </RowFixed>
              </FixedHeightRow>
              <AutoColumn gap="4px">
                <FixedHeightRow>
                  <Text fontSize={16} fontWeight={500}>
                    <>Your pool share:</>
                  </Text>
                  <Text fontSize={16} fontWeight={500}>
                    {poolTokenPercentage ? poolTokenPercentage.toFixed(6) + '%' : '-'}
                  </Text>
                </FixedHeightRow>
                <FixedHeightRow>
                  <Text fontSize={16} fontWeight={500}>
                    {currency0.symbol}:
                  </Text>
                  {token0Deposited ? (
                    <RowFixed>
                      <Text fontSize={16} fontWeight={500} marginLeft="6px">
                        {token0Deposited?.toSignificant(6)}
                      </Text>
                    </RowFixed>
                  ) : (
                    '-'
                  )}
                </FixedHeightRow>
                <FixedHeightRow>
                  <Text fontSize={16} fontWeight={500}>
                    {currency1.symbol}:
                  </Text>
                  {token1Deposited ? (
                    <RowFixed>
                      <Text fontSize={16} fontWeight={500} marginLeft="6px">
                        {token1Deposited?.toSignificant(6)}
                      </Text>
                    </RowFixed>
                  ) : (
                    '-'
                  )}
                </FixedHeightRow>
              </AutoColumn>
              <ButtonSecondary padding="8px" $borderRadius="8px">
                <ExternalLink
                  style={{ width: '100%', textAlign: 'center' }}
                  href={`${import.meta.env.VITE_V2_ANALYTICS}/account/${account}`}
                >
                  <>
                    View fees and analytics<span style={{ fontSize: '13px' }}>&nbsp;↗</span>
                  </>
                </ExternalLink>
              </ButtonSecondary>
            </AutoColumn>
          </LightCard>
        ) : null
        // <LightCard>
        //   <ThemedText.DeprecatedSubHeader style={{ textAlign: 'center' }}>
        //     <span role="img" aria-label="wizard-icon">
        //       ⭐️
        // </span>{' '}
        //     <>
        //       By adding liquidity you&apos;ll earn 0.3% of all trades on this pair proportional to your share of the
        //       pool. Fees are added to the pool, accrue in real time and can be claimed by withdrawing your liquidity.
        //     </>{' '}
        //   </ThemedText.DeprecatedSubHeader>
        // </LightCard>
      }
    </>
  )
}
